import React from "react";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const occasions = [{
  title: "Dedicated Crew Transport",
  description: "Ensure your flight deck and cabin crew reach their destination with absolute punctuality and safety. Our professional chauffeurs provide seamless airside and landside transfers in a fleet of premium, climate-controlled vehicles.",
  image: "https://upright-scorpion.10web.cloud/wp-content/uploads/2026/03/cargo_aircraft.webp?v=wj4ushwupi",
  features: ["24/7 Airside Access", "Multilingual Chauffeurs", "Real-time Flight Tracking", "Executive Fleet Options"],
  link: "/ServicesPage"
}, {
  title: "Premium In-Flight Catering",
  description: "Elevate the dining experience at 30,000 feet with our bespoke catering solutions. From gourmet multi-course meals to specialized dietary requirements, we deliver fresh, high-quality cuisine directly to your aircraft cabin.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1591502/img/corporate_event_dining.png",
  features: ["Gourmet Menu Design", "HACCP Certified Quality", "Last-minute Order Support", "Special Dietary Menus"],
  link: "/ServicesPage"
}, {
  title: "Luxury Hotel Arrangements",
  description: "We partner with the region's finest establishments to provide crews with the rest they deserve. Our concierge team handles all bookings, ensuring preferred rates, crew lounge access, and flexible check-in schedules.",
  image: "https://upright-scorpion.10web.cloud/wp-content/uploads/2026/03/0b7655fd-8b31-4583-ba49-a29c0bd9e50b-scaled.jpg?v=027liwpws8en",
  features: ["Preferred Airline Rates", "Proximity to Airport", "Crew Lounge Access", "Flexible Check-in/out"],
  link: "/ServicesPage"
}, {
  title: "VIP Protocol & Concierge",
  description: "Navigating international arrivals and departures requires precision. Our protocol team manages fast-track immigration, customs assistance, and secure ground handling for diplomats, executives, and high-profile passengers.",
  image: "https://upright-scorpion.10web.cloud/wp-content/uploads/2026/03/corporate_event_dining.webp?v=bg7zubiav2c",
  features: ["Fast-track Immigration", "VIP Meet & Greet", "Diplomatic Clearances", "Secure Ground Logistics"],
  link: "/ServicesPage"
}];
interface OccasionRowProps extends React.HTMLAttributes<HTMLDivElement> {
  item: typeof occasions[0];
  index: number;
}
const OccasionRow = ({
  item,
  index,
  className,
  ...props
}: OccasionRowProps) => {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="39723" ref={ref} className={cn("py-24 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", isEven ? "bg-background" : "bg-secondary/20", className)} {...props}>
      <div className="container mx-auto px-4 md:px-6">
        <div className={cn("flex flex-col gap-12 lg:gap-20 lg:items-center", isEven ? "lg:flex-row" : "lg:flex-row-reverse")}>
          <div className="w-full lg:w-1/2">
            <div className="relative aspect-[4/3] overflow-hidden rounded-md shadow-2xl border border-primary/20 group">
              <div className="absolute inset-0 bg-primary/5 mix-blend-overlay z-10 pointer-events-none" />
              <img src={item.image} alt={item.title} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110" />
              <div className="absolute bottom-6 left-6 right-6 md:left-auto md:right-6 md:w-auto bg-card/80 backdrop-blur-xl border border-primary/30 p-4 rounded-sm z-20">
                <p className="text-primary font-mono text-sm tracking-widest uppercase">
                  Elite support for global carriers
                </p>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-1/2 lg:px-4">
            <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-6 leading-tight tracking-tight">
              {item.title}
            </h3>
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed font-sans">
              {item.description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 mb-10">
              {item.features.map((feature, i) => <div key={i} className="flex items-center gap-3" data-index={i}>
                  <div className="flex-shrink-0 size-6 rounded-sm bg-primary/10 flex items-center justify-center border border-primary/40 shadow-[0_0_15px_rgba(212,175,55,0.15)]">
                    <Check className="size-3.5 text-primary" strokeWidth={3} />
                  </div>
                  <span className="text-foreground/90 font-medium text-base font-sans">
                    {feature}
                  </span>
                </div>)}
            </div>

            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm px-8 h-12 text-base font-bold uppercase tracking-wider shadow-[0_4px_20px_rgba(212,175,55,0.2)] hover:shadow-[0_4px_30px_rgba(212,175,55,0.3)] transition-all">
              <Link to={item.link}>
                Book for {item.title.split(" ")[0]}
                <ArrowRight className="ml-2 size-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>;
};
export default function OccasionsWeServe() {
  return <section id="protocol-crew-support" className="dark bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 pt-24 pb-12 text-center">
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tighter uppercase">
          Protocol & Crew <span className="text-primary">Support</span>
        </h2>
        <p className="text-muted-foreground max-w-3xl mx-auto text-lg md:text-xl leading-relaxed font-sans">
          Providing seamless ground logistics and concierge excellence for international 
          flight crews and VIP passengers at N’djili International Airport and beyond.
        </p>
      </div>

      <div className="flex flex-col">
        {occasions.map((item, index) => <OccasionRow key={index} item={item} index={index} data-index={index} />)}
      </div>
    </section>;
}